import React, { useState } from 'react';
import { FormQuery, SanityFormSection } from 'graphql-types';
import { Field, Form } from 'react-final-form';
import * as styles from './formSection.module.scss';
import { Input } from '../html-tag/Input';
import { usePageContext } from '../context/pageContext';
import addToMailchimp from 'gatsby-plugin-mailchimp';
import { Button } from '../html-tag/Button';
import { Textarea } from '../html-tag/Textarea';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { SelectMenu } from '../html-tag/SelectMenu';
import { PopUp } from '../form/PopUp';
import { AutoComplete } from '../form/AutoComplete';
import { useJsApiLoader } from '@react-google-maps/api';
import { gtagSendEvent } from '../../utils/sendGtagEvent';
import { findTranslation } from '../../utils/findTranslation';

const libraries: any | undefined = ['places'];

interface Props {
  section: SanityFormSection;
  initialValues?: formDATA;
}

export interface formDATA {
  help: string;
  product: string;
  type: string;
  treatment: string;
  firstName: string;
  lastName: string;
  message: string;
  phone: string;
  email: string;
  address: string;
  zipcode: string;
  country: string;
  referer: string;
}

export const FormSection: React.FC<Props> = ({ section }) => {
  const { lang, defaultLang } = usePageContext();
  const [hasSubmit, setHasSubmit] = useState(0);
  const [popUp, setPopUp] = useState<boolean>(false);
  const formButton = useStaticQuery<FormQuery>(FORM);
  const [addressObject, setAddressObject] = useState({
    zipCode: '',
    country: '',
    address: '',
  });

  const onSubmit = (values: formDATA) => {
    const email = values.email;
    const listFields = {
      HELP: (values.help as any)?.value,
      PRODUCT: (values.product as any)?.value,
      TYPE: (values.type as any)?.value,
      TREATMENT: (values.treatment as any)?.value,
      FIRSTNAME: values.firstName,
      LASTNAME: values.lastName,
      MESSAGE: values.message,
      PHONE: values.phone,
      ADDRESS: addressObject.address,
      ZIPCODE: values.zipcode,
      COUNTRY: values.country,
      REFERER: (values.referer as any)?.value,
    };

    addToMailchimp(email, listFields);
    setPopUp(true);
    gtagSendEvent('register', 'contactForm');
    navigate(
      `${lang === defaultLang ? '' : `/${lang}`}/${findTranslation(
        formButton?.sanityPage?.pageSlug ?? [],
        lang,
        defaultLang,
      )}`,
    );
  };

  const helpsOptions =
    section?.formHelpsSection?.map((e) => ({
      ...e,
      value: e?.hubspotValue,
      label: e?._rawLabel?.[lang],
    })) ?? [];

  const productsOptions =
    section?.formProductsSection?.map((e) => ({
      ...e,
      value: e?.hubspotValue,
      label: e?._rawLabel?.[lang],
    })) ?? [];

  const typesOptions =
    section?.formTypesSection?.map((e) => ({
      ...e,
      value: e?.hubspotValue,
      label: e?._rawLabel?.[lang],
    })) ?? [];

  const treatmentsOptions =
    section?.formTreatmentsSection?.map((e) => ({
      ...e,
      value: e?.hubspotValue,
      label: e?._rawLabel?.[lang],
    })) ?? [];

  const refererOptions =
    section?.formRefererOptions?.map((e) => ({
      ...e,
      value: e?.hubspotValue,
      label: e?._rawLabel?.[lang],
    })) ?? [];

  const required = (values: string) =>
    values
      ? undefined
      : findTranslation(formButton.sanityFunctionButton?._rawRequired, lang, defaultLang);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAP_API_KEY as string,
    libraries: libraries,
    language: 'en',
  });

  return (
    <>
      <div className={styles.wrapper}>
        <Form
          onSubmit={onSubmit}
          initialValues={{ zipcode: addressObject.zipCode, country: addressObject.country }}
          keepDirtyOnReinitialize={true}
          render={({ handleSubmit, form, values, errors, submitErrors }) => {
            return (
              <>
                {popUp && (
                  <div className={styles.popUp}>
                    {
                      <PopUp
                        data={findTranslation(section?._rawFormPopupSection, lang, defaultLang)}
                        setPopUp={() => {
                          setPopUp(false);
                        }}
                      />
                    }
                  </div>
                )}
                {section?.isH1 ? (
                  <h1>{findTranslation(section?._rawFormTitleSection, lang, defaultLang)}</h1>
                ) : (
                  <h2>{findTranslation(section?._rawFormTitleSection, lang, defaultLang)}</h2>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit()?.then(() =>
                      Object.keys(values).forEach((key) => {
                        form.change(key as any, undefined);
                        form.resetFieldState(key as any);
                      }),
                    );
                  }}
                >
                  <div className={styles.name}>
                    <fieldset>
                      <Field
                        name="help"
                        component={SelectMenu}
                        options={helpsOptions}
                        label={findTranslation(section?._rawFormHelpSection, lang, defaultLang)}
                        placeholder={section?._rawSelectPlaceholderLabel?.[lang]}
                        validate={required}
                        id="mce-HELP"
                      />
                    </fieldset>

                    <fieldset>
                      <Field
                        name="product"
                        component={SelectMenu}
                        options={productsOptions}
                        label={findTranslation(section?._rawFormProductSection, lang, defaultLang)}
                        placeholder={section?._rawSelectPlaceholderLabel?.[lang]}
                        validate={required}
                        id="mce-PRODUCT"
                      />
                    </fieldset>
                  </div>

                  <fieldset>
                    <Field
                      name="treatment"
                      component={SelectMenu}
                      options={treatmentsOptions}
                      label={findTranslation(section?._rawFormTreatmentSection, lang, defaultLang)}
                      placeholder={section?._rawSelectPlaceholderLabel?.[lang]}
                      id="mce-TREATMENT"
                    />
                  </fieldset>

                  <fieldset>
                    <Field
                      name="type"
                      component={SelectMenu}
                      options={typesOptions}
                      label={findTranslation(section?._rawFormTypeSection, lang, defaultLang)}
                      placeholder={section?._rawSelectPlaceholderLabel?.[lang]}
                      validate={required}
                      id="mce-TYPE"
                    />
                  </fieldset>

                  <fieldset>
                    <Field
                      name="referer"
                      component={SelectMenu}
                      label={findTranslation(
                        section?._rawFormPlaceholderRefererQuestion,
                        lang,
                        defaultLang,
                      )}
                      options={refererOptions}
                      placeholder={section?._rawSelectPlaceholderLabel?.[lang]}
                      id="mce-REFERER"
                    />
                  </fieldset>
                  <div className={styles.name}>
                    <fieldset>
                      <Field
                        name="firstName"
                        component={Input}
                        label={findTranslation(
                          section?._rawFormfirstNameSection,
                          lang,
                          defaultLang,
                        )}
                        placeholder={findTranslation(
                          section?._rawFormPlaceholderfirstNameSection,
                          lang,
                          defaultLang,
                        )}
                        type="text"
                        id="mce-FIRSTNAME"
                        validate={required}
                      />
                    </fieldset>

                    <fieldset>
                      <Field
                        name="lastName"
                        component={Input}
                        label={findTranslation(section?._rawFormlastNameSection, lang, defaultLang)}
                        placeholder={findTranslation(
                          section?._rawFormPlaceholderlastNameSection,
                          lang,
                          defaultLang,
                        )}
                        type="text"
                        id="mce-LASTNAME"
                        validate={required}
                      />
                    </fieldset>
                  </div>

                  <div className={styles.name}>
                    <AutoComplete
                      isLoaded={isLoaded}
                      section={section}
                      setAddressObject={setAddressObject}
                      addressObject={addressObject}
                    />
                    <fieldset>
                      <Field
                        name="zipcode"
                        component={Input}
                        validate={required}
                        label={findTranslation(section?._rawFormZipcodeSection, lang, defaultLang)}
                        placeholder={findTranslation(
                          section?._rawFormPlaceholderZipcodeSection,
                          lang,
                          defaultLang,
                        )}
                        type="text"
                        id="mce-ZIPCODE"
                      />
                    </fieldset>
                  </div>
                  <fieldset>
                    <Field
                      name="country"
                      component={Input}
                      validate={required}
                      label={findTranslation(section?._rawFormCountrySection, lang, defaultLang)}
                      placeholder={findTranslation(
                        section?._rawFormPlaceholderCountrySection,
                        lang,
                        defaultLang,
                      )}
                      type="text"
                      id="mce-COUNTRY"
                    />
                  </fieldset>
                  <div className={styles.name}>
                    <fieldset>
                      <Field
                        name="phone"
                        component={Input}
                        label={findTranslation(section?._rawFormPhoneSection, lang, defaultLang)}
                        placeholder={findTranslation(
                          section?._rawFormPlaceholderPhoneSection,
                          lang,
                          defaultLang,
                        )}
                        type="text"
                        id="mce-PHONE"
                      />
                    </fieldset>

                    <fieldset>
                      <Field
                        name="email"
                        component={Input}
                        label={findTranslation(section?._rawFormEmailSection, lang, defaultLang)}
                        placeholder={findTranslation(
                          section?._rawFormPlaceholderEmailSection,
                          lang,
                          defaultLang,
                        )}
                        type="email"
                        id="mce-EMAIL"
                        validate={required}
                      />
                    </fieldset>
                  </div>
                  <fieldset>
                    <Field
                      name="message"
                      component={Textarea}
                      label={findTranslation(section?._rawFormMessageSection, lang, defaultLang)}
                      placeholder={findTranslation(
                        section?._rawFormPlaceholderMessageSection,
                        lang,
                        defaultLang,
                      )}
                      id="mce-MESSAGE"
                    />
                  </fieldset>

                  {/* <fieldset>
                    <Field
                      name="source"
                      component={Input}
                      label={'source'}
                      value="winback"
                      placeholder={'source'}
                      id="mce-SOURCE"
                    />
                  </fieldset> */}

                  <Field
                    name="optin"
                    component="input"
                    validate={required}
                  >
                    {({ input, meta }) => (
                      <div className={styles.wrapperCheckbox}>
                        <div
                          className={`${styles.checkbox} ${
                            hasSubmit > 0 && meta?.error && styles.checkboxW
                          }`}
                        >
                          <input
                            {...input}
                            type="checkbox"
                            value="checked"
                            className={styles.optin}
                            id="winter"
                          />
                        </div>

                        <h4 className={`${hasSubmit > 0 && meta.error && styles.labelW}`}>
                          {findTranslation(section?._rawFormOptinSection, lang, defaultLang)}
                        </h4>
                      </div>
                    )}
                  </Field>
                  <div className={styles.button}>
                    <Button
                      label={findTranslation(
                        formButton?.sanityFunctionButton?._rawSend,
                        lang,
                        defaultLang,
                      )}
                      type={'submit'}
                      onClick={() => setHasSubmit(hasSubmit + 1)}
                      // disabled={!!errors}
                      dataType={'multi'}
                    />
                  </div>
                </form>
              </>
            );
          }}
        />
      </div>
    </>
  );
};

export const FORM = graphql`
  query Form {
    sanityFunctionButton {
      _rawSend(resolveReferences: { maxDepth: 10 })
      _rawPatient(resolveReferences: { maxDepth: 10 })
      _rawProfessional(resolveReferences: { maxDepth: 10 })
      _rawRequired(resolveReferences: { maxDepth: 10 })
    }
    sanityPage(pageName: { eq: "Thank you" }) {
      pageSlug {
        text
        language
      }
    }
  }
`;
